import React, { useEffect, useMemo, useState } from 'react';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, ArrowsClockwise, X, Camera, PaperPlaneTilt } from '@phosphor-icons/react';
import {
  listPalletMovements, createPalletMovement, voidPalletMovement, getWarehouses, list3plMerchants,
  sendPalletDraftClaims,
} from '../../lib/api';
import { KIND_LABELS, KIND_TONE, fmtDateTime } from './utils';
import ExchangeDialog from './ExchangeDialog';
import PalletPhotoDialog from './PalletPhotoDialog';
import { useAuth } from '../../context/AuthContext';

const KINDS = [
  { value: 'in', label: 'IN — receive' },
  { value: 'out', label: 'OUT — despatch' },
  { value: 'transfer', label: 'Transfer (between warehouses)' },
  { value: 'adjustment', label: 'Adjustment (positive)' },
  { value: 'loss', label: 'Loss' },
  { value: 'damage', label: 'Damage' },
];

export default function MovementsTab({ types, onRefresh }) {
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [warehouses, setWarehouses] = useState([]);
  const [merchants, setMerchants] = useState([]);
  const [filter, setFilter] = useState({ type_id: '', kind: '', merchant_id: '', warehouse_id: '' });
  const [open, setOpen] = useState(false);
  const [exchangeOpen, setExchangeOpen] = useState(false);
  const [photoMovement, setPhotoMovement] = useState(null);
  const [bulkSending, setBulkSending] = useState(false);
  const { user } = useAuth();
  const isManager = ['admin', 'manager'].includes(user?.role);

  const bulkSendDraftClaims = async () => {
    if (!window.confirm(
      'Send emails for every unsent loss/damage movement that has at least one photo attached? This cannot be undone.',
    )) return;
    setBulkSending(true);
    try {
      const r = await sendPalletDraftClaims();
      const d = r.data || {};
      if (d.candidates === 0) {
        toast.info('No draft claims to send — attach photos to loss/damage rows first.');
      } else {
        const bits = [`${d.sent_count} sent`];
        if (d.skipped_count) bits.push(`${d.skipped_count} skipped (no recipient)`);
        if (d.error_count) bits.push(`${d.error_count} errored`);
        toast.success(`Bulk claim run · ${bits.join(' · ')}`);
      }
      await fetchAll();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Bulk send failed');
    } finally {
      setBulkSending(false);
    }
  };
  const [form, setForm] = useState({
    type_id: '', kind: 'in', qty: 1, warehouse_id: '', to_warehouse_id: '',
    merchant_id: '', condition: '', reference: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filter).forEach(([k, v]) => { if (v) params[k] = v; });
      const [mv, wh, mer] = await Promise.all([
        listPalletMovements(params),
        getWarehouses().catch(() => ({ data: [] })),
        list3plMerchants().catch(() => ({ data: [] })),
      ]);
      setMovements(mv.data || []);
      setWarehouses(wh.data || []);
      setMerchants(mer.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not load movements');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [filter]);

  // iter230 — When arriving from the Competition Demo reconcile flow with
  // ?highlight=<reference>, find the matching movement row and scroll it
  // into view. A brief yellow ring is applied via a class that fades out.
  const highlightRef = React.useMemo(() => {
    if (typeof window === 'undefined') return null;
    return new URLSearchParams(window.location.search).get('highlight');
  }, []);
  useEffect(() => {
    if (!highlightRef || loading || movements.length === 0) return;
    const target = movements.find((m) => m.reference === highlightRef);
    if (!target) return;
    // Wait a tick for the row to render.
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="pallet-movement-row-${target.id}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        el.classList.add('ring-4', 'ring-amber-400', 'bg-amber-50');
        setTimeout(() => {
          el.classList.remove('ring-4', 'ring-amber-400', 'bg-amber-50');
        }, 4000);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [highlightRef, loading, movements]);

  // Compute running-balance per row when a single pallet type is selected.
  // Movements arrive newest-first from the API, so we walk them in reverse
  // (oldest → newest) to accumulate the signed_qty, then map by movement id
  // back into the DESC render.
  const IN_KINDS = ['in', 'transfer_in', 'exchange_in', 'adjustment'];
  const OUT_KINDS = ['out', 'transfer_out', 'exchange_out', 'loss', 'damage'];
  const runningById = useMemo(() => {
    if (!filter.type_id) return null;
    const map = new Map();
    let running = 0;
    for (let i = movements.length - 1; i >= 0; i -= 1) {
      const m = movements[i];
      const sign = IN_KINDS.includes(m.kind) ? 1 : OUT_KINDS.includes(m.kind) ? -1 : 0;
      running += sign * (Number(m.qty) || 0);
      map.set(m.id, running);
    }
    return map;
  }, [movements, filter.type_id]);

  const beginAdd = () => {
    const firstActive = types.find((t) => t.is_active);
    setForm({
      type_id: firstActive?.id || '', kind: 'in', qty: 1,
      warehouse_id: '', to_warehouse_id: '', merchant_id: '',
      condition: '', reference: '', notes: '',
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.type_id) return toast.error('Pick a pallet type');
    if (!Number(form.qty) || Number(form.qty) <= 0) return toast.error('Quantity must be > 0');
    if (form.kind === 'transfer' && (!form.warehouse_id || !form.to_warehouse_id))
      return toast.error('Transfer needs source + destination warehouse');
    setSaving(true);
    try {
      const payload = { type_id: form.type_id, kind: form.kind, qty: Number(form.qty) };
      ['warehouse_id', 'to_warehouse_id', 'merchant_id', 'condition', 'reference', 'notes'].forEach((k) => {
        if (form[k] && String(form[k]).trim()) payload[k] = form[k];
      });
      if (payload.kind !== 'transfer') delete payload.to_warehouse_id;
      await createPalletMovement(payload);
      toast.success('Movement posted');
      setOpen(false);
      await fetchAll();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const onVoid = async (m) => {
    if (!window.confirm('Void this movement? It will reverse the balance impact.')) return;
    try {
      await voidPalletMovement(m.id);
      toast.success('Movement voided');
      await fetchAll();
      onRefresh && onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Void failed');
    }
  };

  const merchantName = (mid) => merchants.find((x) => x.id === mid)?.name || (mid ? '—' : '');
  const warehouseName = (wid) => warehouses.find((x) => x.id === wid)?.name || (wid ? '—' : '');

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="text-lg font-bold">Movements</h2>
          <p className="text-xs text-muted-foreground">Every IN / OUT / transfer / adjustment / loss / damage row.</p>
        </div>
        <div className="flex gap-1.5">
          {isManager && (
            <Button onClick={bulkSendDraftClaims} variant="outline" size="sm" disabled={bulkSending}
              data-testid="pallet-bulk-claim-send-btn"
              title="Email every unsent loss/damage movement that has at least one photo attached.">
              <PaperPlaneTilt size={14} className="mr-1" />
              {bulkSending ? 'Sending…' : 'Email all draft claims'}
            </Button>
          )}
          <Button onClick={() => setExchangeOpen(true)} variant="outline" size="sm" data-testid="pallet-exchange-btn">
            <ArrowsClockwise size={14} className="mr-1" /> Truck exchange
          </Button>
          <Button onClick={beginAdd} size="sm" data-testid="pallet-movement-create-btn">
            <Plus size={14} className="mr-1" /> Post movement
          </Button>
        </div>
      </div>

      {/* Filter strip */}
      <div className="flex flex-wrap gap-2 items-center bg-muted/30 border border-border p-2">
        <select className="text-xs border border-border bg-background px-2 py-1"
          value={filter.type_id} onChange={(e) => setFilter({ ...filter, type_id: e.target.value })}
          data-testid="pallet-filter-type">
          <option value="">All types</option>
          {types.map((t) => <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
        </select>
        <select className="text-xs border border-border bg-background px-2 py-1"
          value={filter.kind} onChange={(e) => setFilter({ ...filter, kind: e.target.value })}
          data-testid="pallet-filter-kind">
          <option value="">All kinds</option>
          {Object.entries(KIND_LABELS).map(([k, lbl]) => <option key={k} value={k}>{lbl}</option>)}
        </select>
        <select className="text-xs border border-border bg-background px-2 py-1"
          value={filter.merchant_id} onChange={(e) => setFilter({ ...filter, merchant_id: e.target.value })}
          data-testid="pallet-filter-merchant">
          <option value="">All merchants</option>
          {merchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
        </select>
        <select className="text-xs border border-border bg-background px-2 py-1"
          value={filter.warehouse_id} onChange={(e) => setFilter({ ...filter, warehouse_id: e.target.value })}
          data-testid="pallet-filter-warehouse">
          <option value="">All warehouses</option>
          {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
        </select>
        {Object.values(filter).some(Boolean) && (
          <button className="text-[11px] text-muted-foreground underline"
            onClick={() => setFilter({ type_id: '', kind: '', merchant_id: '', warehouse_id: '' })}>
            Clear
          </button>
        )}
      </div>

      <div className="border border-border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 border-b border-border">
            <tr className="text-left">
              <th className="p-2">When</th>
              <th className="p-2">Type</th>
              <th className="p-2">Kind</th>
              <th className="p-2 text-right">Qty</th>
              {runningById && <th className="p-2 text-right" title="Running balance for the filtered pallet type">Running</th>}
              <th className="p-2">Warehouse</th>
              <th className="p-2">Merchant</th>
              <th className="p-2">Reference</th>
              <th className="p-2 w-20 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={runningById ? 9 : 8} className="p-6 text-center text-muted-foreground">Loading…</td></tr>
            )}
            {!loading && movements.length === 0 && (
              <tr><td colSpan={runningById ? 9 : 8} className="p-6 text-center text-muted-foreground">
                No movements match the current filter.
              </td></tr>
            )}
            {!loading && movements.map((m) => (
              <tr key={m.id} data-testid={`pallet-movement-row-${m.id}`}
                className="border-b border-border last:border-b-0 hover:bg-muted/30">
                <td className="p-2 text-xs whitespace-nowrap">{fmtDateTime(m.created_at)}</td>
                <td className="p-2 font-mono text-xs">{m.type_code}</td>
                <td className="p-2">
                  <span className={`text-[10px] uppercase tracking-wider border px-1.5 py-0.5 ${KIND_TONE[m.kind] || 'bg-slate-100 text-slate-700 border-slate-200'}`}>
                    {KIND_LABELS[m.kind] || m.kind}
                  </span>
                </td>
                <td className="p-2 text-right font-mono">{m.qty}</td>
                {runningById && (
                  <td className="p-2 text-right font-mono font-bold" data-testid={`pallet-movement-running-${m.id}`}>
                    {runningById.get(m.id) ?? '—'}
                  </td>
                )}
                <td className="p-2 text-xs">{warehouseName(m.warehouse_id)}</td>
                <td className="p-2 text-xs">{merchantName(m.merchant_id)}</td>
                <td className="p-2 text-xs">
                  {m.reference || (m.exchange_id ? <em className="text-violet-700">Truck exchange</em> : '—')}
                  {m.notes && <div className="text-[11px] text-muted-foreground">{m.notes}</div>}
                </td>
                <td className="p-2 text-right">
                  <Button size="sm" variant="outline"
                    className={`mr-1 ${(m.kind === 'loss' || m.kind === 'damage' || (m.photos && m.photos.length)) ? 'text-rose-700' : ''}`}
                    onClick={() => setPhotoMovement(m)}
                    title={`${(m.photos && m.photos.length) || 0} photo(s) attached`}
                    data-testid={`pallet-movement-photos-${m.id}`}>
                    <Camera size={12} />
                    {m.photos && m.photos.length > 0 && (
                      <span className="ml-1 text-[10px] font-mono">{m.photos.length}</span>
                    )}
                  </Button>
                  <Button size="sm" variant="outline" className="text-rose-700"
                    onClick={() => onVoid(m)} data-testid={`pallet-movement-void-${m.id}`}>
                    <X size={12} />
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg" data-testid="pallet-movement-dialog">
          <DialogHeader>
            <DialogTitle>Post pallet movement</DialogTitle>
            <DialogDescription>
              Single atomic row. Use Truck exchange for paired in/out swaps at the dock.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Pallet type *</Label>
                <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                  value={form.type_id} onChange={(e) => setForm({ ...form, type_id: e.target.value })}
                  data-testid="movement-form-type">
                  <option value="">Select…</option>
                  {types.filter((t) => t.is_active).map((t) =>
                    <option key={t.id} value={t.id}>{t.code} · {t.name}</option>)}
                </select>
              </div>
              <div>
                <Label>Kind *</Label>
                <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                  value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })}
                  data-testid="movement-form-kind">
                  {KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Qty *</Label>
                <Input type="number" min="1" value={form.qty}
                  onChange={(e) => setForm({ ...form, qty: e.target.value })}
                  data-testid="movement-form-qty" />
              </div>
              <div>
                <Label>Condition (optional)</Label>
                <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                  value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })}
                  data-testid="movement-form-condition">
                  <option value="">—</option>
                  <option value="A">A · Good</option>
                  <option value="B">B · Fair</option>
                  <option value="C">C · Damaged</option>
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>{form.kind === 'transfer' ? 'From warehouse *' : 'Warehouse'}</Label>
                <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                  value={form.warehouse_id} onChange={(e) => setForm({ ...form, warehouse_id: e.target.value })}
                  data-testid="movement-form-warehouse">
                  <option value="">—</option>
                  {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                </select>
              </div>
              {form.kind === 'transfer' ? (
                <div>
                  <Label>To warehouse *</Label>
                  <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                    value={form.to_warehouse_id} onChange={(e) => setForm({ ...form, to_warehouse_id: e.target.value })}
                    data-testid="movement-form-to-warehouse">
                    <option value="">—</option>
                    {warehouses.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}
                  </select>
                </div>
              ) : (
                <div>
                  <Label>Merchant (optional)</Label>
                  <select className="w-full border border-border bg-background h-9 px-2 text-sm"
                    value={form.merchant_id} onChange={(e) => setForm({ ...form, merchant_id: e.target.value })}
                    data-testid="movement-form-merchant">
                    <option value="">—</option>
                    {merchants.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                  </select>
                </div>
              )}
            </div>
            <div>
              <Label>Reference</Label>
              <Input value={form.reference} onChange={(e) => setForm({ ...form, reference: e.target.value })}
                placeholder="PO, ASN, container, docket…" data-testid="movement-form-reference" />
            </div>
            <div>
              <Label>Notes</Label>
              <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                className="w-full border border-border bg-background p-2 text-sm" rows={2}
                data-testid="movement-form-notes" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={submit} disabled={saving} data-testid="movement-form-submit">
              {saving ? 'Posting…' : 'Post movement'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ExchangeDialog
        open={exchangeOpen} setOpen={setExchangeOpen}
        types={types} warehouses={warehouses} merchants={merchants}
        onSaved={async () => { await fetchAll(); onRefresh && onRefresh(); }}
      />

      <PalletPhotoDialog
        open={!!photoMovement}
        setOpen={(v) => { if (!v) setPhotoMovement(null); }}
        movement={photoMovement}
        isManager={isManager}
        onRefresh={() => fetchAll()}
      />
    </div>
  );
}
